import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import {
  LayoutDashboard, Building2, CalendarDays, Users, Lock, ScrollText,
  LogOut, ShieldCheck, MessageSquare, AlertTriangle, Clock, Briefcase, Home, ClipboardList, MapPin, ShieldAlert, ArrowLeft, Settings, Megaphone, HeartPulse, Layers,
  PanelLeftClose, PanelLeftOpen, Palette, Repeat, AlarmClock,
} from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
  DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { GlobalSearch } from "@/components/GlobalSearch";
import { NotificationsBell } from "@/components/NotificationsBell";
import { AlertPopup } from "@/components/AlertPopup";
import { FloatingMessages } from "@/components/FloatingMessages";
import client from "@/lib/api";
import { toast } from "sonner";
import { usePoll } from "@/hooks/usePoll";

const ROLE_LABEL = { owner: "Owner", admin: "Admin", supervisor: "Supervisor", office: "Office", cleaner: "Cleaner" };

export default function AppLayout() {
  const { user, logout, can, messagingEnabled } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("sidebarCollapsed") === "1");
  const toggleCollapsed = () => setCollapsed((v) => { localStorage.setItem("sidebarCollapsed", v ? "0" : "1"); return !v; });

  // Force the desktop layout only inside the app (not on the login page) when on a
  // phone/tablet. Restored to device-width on unmount so login stays a normal mobile page.
  useEffect(() => {
    const vp = document.getElementById("vp");
    const minDim = Math.min(window.screen.width, window.screen.height);
    if (!vp || !minDim || minDim >= 1024) return undefined;
    vp.setAttribute("content", "width=1000, viewport-fit=cover");
    document.documentElement.classList.add("force-desktop");
    return () => {
      vp.setAttribute("content", "width=device-width, initial-scale=1, viewport-fit=cover");
      document.documentElement.classList.remove("force-desktop");
    };
  }, []);

  // Global new-message alert: poll conversations on every page; toast when a new
  // unread message (addressed to me, not oversight) arrives.
  const msgSeenRef = useRef(null);
  usePoll(async () => {
    if (!can("message.view") || !messagingEnabled) return;
    try {
      const { data } = await client.get("/messages/conversations");
      const mine = (data || []).filter((c) => !c.oversight && c.unread > 0);
      const newest = mine.reduce((a, c) => (!a || c.lastAt > a.lastAt ? c : a), null);
      if (!newest) return;
      if (msgSeenRef.current === null) { msgSeenRef.current = newest.lastAt; return; }
      if (newest.lastAt > msgSeenRef.current) {
        msgSeenRef.current = newest.lastAt;
        if (location.pathname !== "/messages") {
          toast.message(`${t("msg.newAlert", "New message")}${newest.lastSender ? ` · ${newest.lastSender}` : ""}`,
            { description: newest.lastBody, action: { label: t("msg.view", "View"), onClick: () => navigate("/messages") } });
        }
      }
    } catch { /* noop */ }
  }, 12000);
  useEffect(() => { if (location.pathname === "/messages") msgSeenRef.current = null; }, [location.pathname]);

  const nav = [
    { to: "/", key: "nav.dashboard", label: "Dashboard", icon: LayoutDashboard, show: true, testid: "sidebar-nav-dashboard", end: true },
    { to: "/messages", key: "nav.messages", label: "Messages", icon: MessageSquare, show: can("message.view") && messagingEnabled, testid: "sidebar-nav-messages", section: "core" },
    { to: "/issues", key: "nav.issues", label: "Issues", icon: AlertTriangle, show: can("issue.view"), testid: "sidebar-nav-issues", section: "core" },
    { to: "/sites", key: "nav.sites", label: "Sites", icon: Building2, show: can("site.view"), testid: "sidebar-nav-sites", section: "connect" },
    { to: "/schedule", key: "nav.schedule", label: "Schedule", icon: CalendarDays, show: can("schedule.view"), testid: "sidebar-nav-schedule", section: "connect" },
    { to: "/attendance", key: "nav.attendance", label: "Attendance", icon: Clock, show: can("attendance.view") || can("attendance.manage"), testid: "sidebar-nav-attendance", section: "connect" },
    { to: "/early-arrivals", key: "nav.earlyArrivals", label: "Early Arrivals", icon: AlarmClock, show: can("attendance.view"), testid: "sidebar-nav-early-arrivals", section: "connect" },
    { to: "/term-calendars", key: "nav.termCals", label: "Term Calendars", icon: CalendarDays, show: can("site.manage"), testid: "sidebar-nav-term-calendars", section: "connect" },
    { to: "/clients", key: "nav.clients", label: "Clients", icon: Briefcase, show: can("client.view"), testid: "sidebar-nav-clients", section: "field" },
    { to: "/properties", key: "nav.properties", label: "Properties", icon: Home, show: can("property.view"), testid: "sidebar-nav-properties", section: "field" },
    { to: "/jobs", key: "nav.jobs", label: "Jobs", icon: ClipboardList, show: can("job.view"), testid: "sidebar-nav-jobs", section: "field" },
    { to: "/field-map", key: "nav.fieldMap", label: "Field Map", icon: MapPin, show: can("job.view"), testid: "sidebar-nav-field-map", section: "field" },
    { to: "/communal-list", key: "nav.communal", label: "Communal List", icon: Repeat, show: can("property.view"), testid: "sidebar-nav-communal", section: "field" },
    { to: "/timesheets", key: "nav.timesheets", label: "Timesheets", icon: Clock, show: can("timesheet.approve"), testid: "sidebar-nav-timesheets", section: "admin" },
    { to: "/timesheets-all", key: "nav.allHours", label: "All Hours", icon: Layers, show: can("timesheet.approve"), testid: "sidebar-nav-all-hours", section: "admin" },
    { to: "/alerts", key: "nav.alerts", label: "Alerts", icon: Megaphone, show: can("alert.send"), testid: "sidebar-nav-alerts", section: "admin" },
    { to: "/staff", key: "nav.staff", label: "Staff & Access", icon: Users, show: can("user.view"), testid: "sidebar-nav-staff", section: "admin" },
    { to: "/staff-colours", key: "nav.staffColours", label: "Staff Colours", icon: Palette, show: can("user.view"), testid: "sidebar-nav-staff-colours", section: "admin" },
    { to: "/setup-health", key: "nav.setupHealth", label: "Setup Health", icon: HeartPulse, show: user?.role === "owner" || user?.role === "admin", testid: "sidebar-nav-setup-health", section: "admin" },
    { to: "/forms", key: "nav.forms", label: "Forms & Checklists", icon: ClipboardList, show: can("checklist.manage"), testid: "sidebar-nav-forms", section: "admin" },
    { to: "/payroll", key: "nav.payroll", label: "Payroll Vault", icon: Lock, show: user?.role === "owner", testid: "sidebar-nav-payroll-vault", vault: true, section: "admin" },
    { to: "/audit", key: "nav.audit", label: "Audit Trail", icon: ScrollText, show: can("audit.view"), testid: "sidebar-nav-audit", section: "admin" },
    { to: "/admin/test-cleanup", key: "nav.testCleanup", label: "Test Data Cleanup", icon: ShieldAlert, show: user?.role === "owner", testid: "sidebar-nav-test-cleanup", section: "admin" },
  ];

  const SECTION_LABEL = {
    core: t("nav.sec.core", "Core / Operations"),
    connect: t("connect.label", "iClean Connect"),
    field: t("field.label", "iClean Field"),
    admin: t("nav.sec.admin", "Shared / Admin"),
  };

  const initials = (user?.displayName || "?").split(" ").map((s) => s[0]).slice(0, 2).join("");

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="app-shell flex overflow-hidden bg-slate-50">
      {/* Sidebar */}
      <aside className={`${collapsed ? "w-16" : "w-52"} flex-shrink-0 hidden md:flex flex-col bg-brand-ink text-slate-300 transition-[width] duration-200`}>
        <div className={`h-14 flex items-center border-b border-white/10 ${collapsed ? "justify-center px-0" : "justify-between px-4"}`}>
          {!collapsed && (
            <div className="bg-white rounded-lg px-2.5 py-1">
              <img src="/iclean-logo.webp" alt="iClean" className="h-6 w-auto" />
            </div>
          )}
          <button data-testid="sidebar-collapse-toggle" onClick={toggleCollapsed}
            title={collapsed ? t("layout.expand", "Expand menu") : t("layout.collapse", "Collapse menu")}
            className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:bg-white/10 hover:text-white transition">
            {collapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>
        <nav className={`flex-1 py-2 space-y-0.5 overflow-y-auto overflow-x-hidden ${collapsed ? "px-2" : "px-2"}`} data-testid="sidebar-scroll">
          {(() => {
            let last = null;
            const out = [];
            nav.filter((n) => n.show).forEach((n) => {
              const sec = n.section || "general";
              if (SECTION_LABEL[sec] && sec !== last) {
                out.push(
                  collapsed ? (
                    last === null ? null : <div key={`sec-${sec}`} className="my-1.5 mx-2 border-t border-white/10" />
                  ) : (
                    <div key={`sec-${sec}`} data-testid={`sidebar-section-${sec}`} className="px-2.5 pt-3 pb-0.5 text-[9px] font-bold uppercase tracking-wider text-slate-500">
                      {SECTION_LABEL[sec]}
                    </div>
                  )
                );
              }
              last = sec;
              out.push(
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={n.testid}
                  title={collapsed ? t(n.key, n.label) : undefined}
                  className={({ isActive }) =>
                    `sidebar-link flex items-center rounded-md text-[13px] font-medium ${collapsed ? "justify-center px-0 py-2" : "gap-2.5 px-2.5 py-1.5"} ${
                      isActive
                        ? n.vault
                          ? "bg-amber-500/15 text-amber-300"
                          : "bg-brand/20 text-brand"
                        : "text-slate-400 hover:bg-white/5 hover:text-white"
                    }`
                  }
                >
                  <n.icon className="h-4 w-4 flex-shrink-0" />
                  {!collapsed && <span className="truncate">{t(n.key, n.label)}</span>}
                  {!collapsed && n.vault && <Lock className="h-3 w-3 ml-auto text-amber-400/70" />}
                </NavLink>
              );
            });
            return out;
          })()}
        </nav>
        <div className={`py-2.5 border-t border-white/10 text-[10px] text-slate-500 ${collapsed ? "flex justify-center" : "px-3"}`}>
          {collapsed ? (
            <ShieldCheck className="h-4 w-4 text-teal-400" title={t("layout.rbac", "Server-side RBAC active")} />
          ) : (
            <p className="flex items-center gap-1.5"><ShieldCheck className="h-3 w-3 text-teal-400" /> {t("layout.rbac", "Server-side RBAC active")}</p>
          )}
        </div>
      </aside>

      {/* Main */}
      <AlertPopup />
      <FloatingMessages />
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        <header className="h-14 flex items-center justify-between px-4 sm:px-6 bg-white border-b border-slate-200 flex-shrink-0 z-20">
          <div className="flex items-center gap-2 flex-shrink-0">
            {location.pathname !== "/" && (
              <button data-testid="header-back-btn" onClick={() => navigate(-1)} title={t("common.back", "Back")}
                className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-brand transition">
                <ArrowLeft className="h-4 w-4" />
              </button>
            )}
            <h1 className="hidden lg:block font-display font-bold text-slate-900 text-base">iClean Portal</h1>
          </div>
          <div className="flex-1 min-w-0 flex justify-center px-2 sm:px-4">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0">
            <NotificationsBell />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="user-menu-trigger" className="flex items-center gap-2 pl-2 pr-1 py-1 rounded-full hover:bg-slate-100">
                  <div className="h-8 w-8 rounded-full bg-gradient-to-br from-brand to-brand-dark text-white flex items-center justify-center text-xs font-bold uppercase">
                    {initials}
                  </div>
                  <div className="hidden sm:block text-left leading-tight pr-1">
                    <p className="text-xs font-semibold text-slate-900">{user?.displayName}</p>
                    <p className="text-[10px] text-slate-500">{t(`role.${user?.role}`, ROLE_LABEL[user?.role] || user?.role)}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <DropdownMenuLabel className="text-xs">{user?.email}</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="settings-menu-item" onClick={() => navigate("/settings")}>
                  <Settings className="h-4 w-4 mr-2" /> {t("nav.settings", "Settings")}
                </DropdownMenuItem>
                <DropdownMenuItem data-testid="logout-btn" onClick={handleLogout} className="text-red-600">
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto" data-testid="main-scroll">
          <div className="p-3 sm:p-4 lg:p-6 max-w-[1400px] w-full mx-auto">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
